import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  InternalInventoryLookupQuery,
  RfqFulfillFromInventoryQuery,
  useInternalInventoryLookupQuery,
  useClueInventoryInfoQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useMsgs from '@/gf/hooks/useMsgs'
import useForm from './useForm'

import LocationField from '@/buyers/components/LocationField'
import Action from '@/gf/components/Action'
import Card from '@/gf/components/Card'
import Form from '@/gf/components/Form'
import PartsForm from '../PartsForm'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/gf/components/next/Tooltip'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import FleetioLocationSelector from '@/buyers/pages/Order/YourOrder/FleetioLocationSelector'
import { isRfqClosed } from '@/buyers/modules/RfqClosed'

type Rfq = RfqFulfillFromInventoryQuery['rfqs'][number]

const FulfillFromInventoryForm = ({
  rfq,
  initPickupLocationId,
}: {
  rfq: Rfq
  initPickupLocationId: string | null
}) => {
  const [ok, setOk] = useState(false)
  const navigate = useNavigate()
  const [_, msgr] = useMsgs()

  const { fields, submitting, errors, updateFields, addPart, removePart, updatePart, submit } =
    useForm({
      rfq,
      initPickupLocationId,
      onOk: () => {
        msgr.add('Quote created.', 'positive')
        setOk(true)
      },
    })

  // Calculate hasUnfulfilledQuantities for close-request modal
  const hasUnfulfilledQuantities = useMemo(
    () =>
      rfq.parts.some((originalPart) => {
        const fulfilledPart = fields.parts.find((p) => p.rfqPartId === originalPart.id)
        const fulfilledQty = fulfilledPart?.quantity ?? 0
        return fulfilledQty < originalPart.quantity
      }),
    [rfq.parts, fields.parts]
  )

  useEffect(() => {
    if (ok) {
      if (isRfqClosed(rfq)) {
        navigate(`/rfqs/${rfq.id}`)
      } else {
        navigate('close-request', { state: { hasUnfulfilledQuantities } })
      }
    }
  }, [ok])

  const updateFormWithInternalInventory = (
    productsFromInventory: InternalInventoryLookupQuery['inventory']['results']
  ) => {
    const newParts = fields.parts.map((pr) => {
      const inInventory = productsFromInventory.find((p) => p.mpn === pr.mpn)
      return inInventory
        ? {
            ...pr,
            mpn: inInventory.mpn || '',
            name: inInventory.name || '',
          }
        : pr
    })

    updateFields({ parts: newParts })
  }

  useInternalInventoryLookupQuery({
    client: useGqlClient(),
    variables: { partNumbers: rfq.parts.map((pr) => pr.mpn) },
    onCompleted(data) {
      updateFormWithInternalInventory(data.inventory.results)
    },
  })

  // Pre-fill each part's unit price from Clue's inventory feed so the buyer
  // can review/override before the internal quote is created. The cost then
  // flows downstream to ClueSyncPartOrderStatus's ORDERED partOrder write-back.
  // We block submit on `loading`/`error` below so a still-hydrating Clue
  // response can't silently fall back to $0 in useForm.
  const { loading: cluePricingLoading, error: cluePricingError } = useClueInventoryInfoQuery({
    client: useGqlClient(),
    variables: { requestForQuoteId: rfq.id },
    skip: !rfq.cluePollingEnabled,
    onCompleted(data) {
      const newParts = fields.parts.map((pr) => {
        if (pr.unitPrice) return pr
        const clueRow = data.clueInventoryInfo.find((row) => row.id === pr.rfqPartId)
        return clueRow?.unitCost ? { ...pr, unitPrice: clueRow.unitCost } : pr
      })
      updateFields({ parts: newParts })
    },
  })

  const hasFleetio = rfq.orgMachines.some((om) => om.fleetioId)
  const hasClue = rfq.cluePollingEnabled

  const initialLocationCode = rfq.shippingLocation?.code

  const validate = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!fields.pickupLocationId) {
      msgr.add('Pickup location is required', 'negative')
      e.stopPropagation()
      e.preventDefault()
      return false
    }
    if (hasFleetio && !fields.fleetioSelectedLocationId) {
      msgr.add('Fleetio location should be selected', 'negative')
      e.stopPropagation()
      e.preventDefault()
      return false
    }
    if (cluePricingLoading) {
      msgr.add('Loading inventory pricing — please wait a moment.', 'negative')
      e.stopPropagation()
      e.preventDefault()
      return false
    }
    if (cluePricingError) {
      msgr.add(
        "Couldn't load inventory pricing from Clue. Enter unit prices manually before submitting.",
        'negative'
      )
      e.stopPropagation()
      e.preventDefault()
      return false
    }
    return true
  }

  return (
    <Form onSubmit={submit} className="max-w-5xl mt-4">
      <Card>
        <Card.Section>
          <div className="space-y-8">
            <PartsForm
              parts={fields.parts}
              errors={errors.parts}
              updatePart={updatePart}
              removePart={removePart}
              addPart={addPart}
              autoCompleteFromInventory
              useFleetioPartSearch={hasFleetio}
              useCluePartSearch={hasClue && !hasFleetio}
            />

            <LocationField
              label="Pickup Address"
              desc="This is the address where the parts listed above are located, i.e. your parts room."
              locationId={fields.pickupLocationId}
              onLocationIdChange={(pickupLocationId) => updateFields({ pickupLocationId })}
              errors={{ locationId: errors.pickupLocationId }}
            />

            {hasFleetio && (
              <div className="space-y-1 py-4 max-w-md">
                <div className="flex items-center">
                  <span className=" font-medium mr-1">Required for Fleetio WO</span>
                  <Tooltip placement="bottom-end">
                    <TooltipTrigger>
                      <InformationCircleIcon className="h-5 w-5 text-gray-600" />
                    </TooltipTrigger>
                    <TooltipContent className="w-52 p-3 bg-gray-700 border border-gray-900 rounded shadow-sm text-sm text-gray-100">
                      Create Location in Fleetio if you cannot find it
                    </TooltipContent>
                  </Tooltip>
                </div>

                <div className="space-y-1 pt-4">
                  <FleetioLocationSelector
                    initialSelection={initialLocationCode}
                    selectedId={fields.fleetioSelectedLocationId}
                    onChange={(id) => updateFields({ fleetioSelectedLocationId: id })}
                  />
                </div>
              </div>
            )}

            <Action.P
              type="submit"
              performing={submitting}
              disabled={cluePricingLoading}
              onClick={validate}
            >
              Submit
            </Action.P>
          </div>
        </Card.Section>
      </Card>
    </Form>
  )
}

export default FulfillFromInventoryForm
